import React, { useEffect, useState } from "react";
import {
  CopyOutlined,
  GlobalOutlined,
  LinkOutlined,
  LockOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Divider, Input, Modal, Row, Space, Switch, Typography, message } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";

const { Text } = Typography;

/**
 * PublicResumeModal Component
 *
 * Single-page modal for resume privacy settings with public link sharing
 */
const PublicResumeModal = ({ visible, onClose, resumeId, currentPublicStatus = false, onPublicStatusChange }) => {
  const fetch = useAuthenticatedFetch();

  // Modal state management
  const [loading, setLoading] = useState(false);
  const [publicUrl, setPublicUrl] = useState("");
  const [copied, setCopied] = useState(false);

  // Privacy settings state - email, phone, and address can be hidden
  const [privacySettings, setPrivacySettings] = useState({
    showEmail: true,
    showPhone: true,
    showAddress: true,
  });

  // Load current settings when modal opens
  useEffect(() => {
    if (visible) {
      setCopied(false);
      loadCurrentSettings();
    }
  }, [visible]);

  // Load current public settings
  const loadCurrentSettings = async () => {
    try {
      const response = await fetch.get(`/resume/${resumeId}/public-data`);
      if (response.data) {
        setPrivacySettings({
          showEmail: response.data.publicSettings?.showEmail ?? true,
          showPhone: response.data.publicSettings?.showPhone ?? true,
          showAddress: response.data.publicSettings?.showAddress ?? true,
        });
        if (response.data.publicUrl) {
          setPublicUrl(response.data.publicUrl);
        }
      }
    } catch (error) {
      console.error("Error loading public settings:", error);
    }
  };

  // Handle making resume public or updating settings
  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      if (!currentPublicStatus) {
        // Make public
        const response = await fetch.post(`/resume/${resumeId}/make-public`, {
          publicSettings: privacySettings,
        });

        if (response.data) {
          setPublicUrl(response.data.publicUrl);
          message.success("Resume is now public!");

          // Notify parent component
          if (onPublicStatusChange) {
            onPublicStatusChange(true, response.data);
          }
        }
      } else {
        // Update settings
        await fetch.put(`/resume/${resumeId}/public-settings`, {
          publicSettings: privacySettings,
        });

        message.success("Privacy settings updated!");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      message.error("Failed to save settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Copy URL to clipboard
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    message.success("Link copied to clipboard!");

    setTimeout(() => setCopied(false), 2000);
  };

  // View public page
  const handleViewPublic = () => {
    window.open(publicUrl, "_blank");
  };

  // Handle making resume private
  const handleMakePrivate = async () => {
    setLoading(true);
    try {
      await fetch.post(`/resume/${resumeId}/make-private`);
      setPublicUrl("");
      message.success("Resume is now private!");

      // Notify parent component
      if (onPublicStatusChange) {
        onPublicStatusChange(false, null);
      }
    } catch (error) {
      console.error("Error making resume private:", error);
      message.error("Failed to make resume private. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle change
  const handleTogglePublic = (checked) => {
    if (checked) {
      handleSaveSettings();
    } else {
      handleMakePrivate();
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      title={
        <Space>
          <GlobalOutlined style={{ color: "#1890ff" }} />
          <span>Public Resume Settings</span>
        </Space>
      }
    >
      <div style={{ padding: "10px 0" }}>
        {/* Public/Private Toggle */}
        <Card
          size="small"
          style={{
            marginBottom: 24,
            border: currentPublicStatus ? '2px solid #52c41a' : '2px solid #d9d9d9',
            backgroundColor: currentPublicStatus ? '#f6ffed' : '#fafafa'
          }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Space direction="vertical" size={0}>
                <Space>
                  {currentPublicStatus ? (
                    <GlobalOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                  ) : (
                    <LockOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />
                  )}
                  <Text strong style={{ fontSize: 16 }}>
                    {currentPublicStatus ? 'Public Resume' : 'Private Resume'}
                  </Text>
                </Space>
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 26 }}>
                  {currentPublicStatus
                    ? 'Anyone with the link can view your resume'
                    : 'Only you can view your resume'}
                </Text>
              </Space>
            </Col>
            <Col>
              <Switch
                checked={currentPublicStatus}
                onChange={handleTogglePublic}
                loading={loading}
                checkedChildren={<GlobalOutlined />}
                unCheckedChildren={<LockOutlined />}
                style={{
                  backgroundColor: currentPublicStatus ? '#52c41a' : undefined
                }}
              />
            </Col>
          </Row>
        </Card>

        {/* Public Link Section - Only show if already public */}
        {currentPublicStatus && publicUrl && (
          <Card style={{ marginBottom: 24, backgroundColor: "#f5f5f5" }}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Text type="secondary" strong>
                Public Link
              </Text>
              <Input
                value={publicUrl}
                readOnly
                style={{ fontFamily: "monospace", fontSize: 12 }}
                addonAfter={
                  <Button
                    type="text"
                    icon={copied ? <CopyOutlined style={{ color: "#52c41a" }} /> : <CopyOutlined />}
                    onClick={handleCopyUrl}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                }
              />
              <Space size="small" style={{ width: "100%", justifyContent: "center" }}>
                <Button size="small" icon={<LinkOutlined />} onClick={handleViewPublic}>
                  View Public Page
                </Button>
                <Button
                  size="small"
                  icon={<ShareAltOutlined />}
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: "My Resume",
                        text: "Check out my resume",
                        url: publicUrl,
                      });
                    } else {
                      handleCopyUrl();
                    }
                  }}
                >
                  Share
                </Button>
              </Space>
            </Space>
          </Card>
        )}

        {/* Contact Visibility Settings - Only show if public */}
        {currentPublicStatus && (
          <>
            <Divider orientation="left" style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 14 }}>Contact Visibility Settings</Text>
            </Divider>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Text>Show Email Address</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Display email on public resume
                    </Text>
                  </Col>
                  <Col>
                    <Switch
                      checked={privacySettings.showEmail}
                      onChange={(checked) =>
                        setPrivacySettings((prev) => ({ ...prev, showEmail: checked }))
                      }
                    />
                  </Col>
                </Row>

                <Divider style={{ margin: "8px 0" }} />

                <Row justify="space-between" align="middle">
                  <Col>
                    <Text>Show Phone Number</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Display phone on public resume
                    </Text>
                  </Col>
                  <Col>
                    <Switch
                      checked={privacySettings.showPhone}
                      onChange={(checked) =>
                        setPrivacySettings((prev) => ({ ...prev, showPhone: checked }))
                      }
                    />
                  </Col>
                </Row>

                <Divider style={{ margin: "8px 0" }} />

                <Row justify="space-between" align="middle">
                  <Col>
                    <Text>Show Address/Location</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Display address on public resume
                    </Text>
                  </Col>
                  <Col>
                    <Switch
                      checked={privacySettings.showAddress}
                      onChange={(checked) =>
                        setPrivacySettings((prev) => ({ ...prev, showAddress: checked }))
                      }
                    />
                  </Col>
                </Row>
              </Space>
            </Card>

            <Button
              type="primary"
              onClick={handleSaveSettings}
              loading={loading}
              style={{ width: "100%" }}
            >
              Save Contact Settings
            </Button>
          </>
        )}

        {/* Action Buttons */}
        <Space style={{ width: "100%", justifyContent: "flex-end", marginTop: 16 }}>
          <Button onClick={onClose}>Close</Button>
        </Space>
      </div>
    </Modal>
  );
};

export default PublicResumeModal;
